import {
  HrefOptions,
  Ng2StateDeclaration,
  RawParams,
  StateOrName,
  Transition
} from '@uirouter/angular';
import { StateDeclaration, StateObject, UIRouter } from '@uirouter/core';
import { AppRouter, UrlMatcherFn } from '@tdwb/core';

export const uiRouterInitializerFactory = (uiRouter: UIRouter, document: Document): AppRouter => {
  return new AppUIRouter(uiRouter, document);
};

export class AppUIRouter implements AppRouter {
  router: UIRouter;
  document: Document;

  constructor(private uiRouter: UIRouter, document: Document) {
    this.router = uiRouter;
    this.document = document;
  }

  registerPageInitialLoad(onLoad: () => void): void {
    if(onLoad) {
      this.uiRouter.transitionService.onSuccess({ to: 'page.**' }, () => {
        onLoad();
      }, { invokeLimit: 1, priority: 10 })
    }
  }

  registerPagesLoad(onLoad: () => void): void {
    if(onLoad) {
      this.uiRouter.transitionService.onSuccess({ to: 'page.**' }, () => {
        onLoad();
      }, { priority: 1 });
    }
  }

  registerModalClose(name: string, onClose: () => void): void {
    if(name && onClose) {
      this.uiRouter.transitionService.onSuccess({ exiting: name }, (): void => {
        onClose();
      }, { invokeLimit: 1 });
    }
  }

  registerFutureStates(states: Array<Ng2StateDeclaration>): void {
    states.forEach((state: Ng2StateDeclaration): void => {
      this.router.stateRegistry.register(state);
    });
  }

  listenForUrlChange(): void {
    this.router.urlService.listen();
    this.router.urlService.sync();
  }

  disableListenForUrlChange(): void {
    this.router.urlService.deferIntercept();
  }

  handleFocusOnPageTransition(): void {
    this.router.transitionService.onSuccess({ to: 'page.**', from: 'page.**' },
      (transition: Transition): void => {
        this.handleFocusOnRootStateChanged(transition);
      },
      {
        priority: 1
      });
  }

  getUrlMatcherFn(transition: Transition): UrlMatcherFn {
    return transition?.to()?.$$state()?.url?.exec.bind(transition?.to()?.$$state()?.url);
  }

  private handleFocusOnRootStateChanged(transition: Transition): void {
    const element = this.document.getElementById('td-wb-content');
    if (this.isParentRootStateChanged(transition)
      && element !== undefined && element !== null) {
      element.focus();
    }
  }

  private isParentRootStateChanged(transition: Transition): boolean {
    let isChanged = false;

    const to  = transition.to();
    const from  = transition.from();
    const toRootParentState = transition.to().data?.rootParentState ?? 'page';
    const fromRootParentState = transition.from().data?.rootParentState ?? 'page';

    if (toRootParentState === fromRootParentState) {
      const toName = to.name.replace(toRootParentState + '.', '').split('.');
      const fromName = from.name.replace(fromRootParentState + '.', '').split('.');
      if (toName[0] !== fromName[0]) {
        isChanged = true;
      }
    } else {
      isChanged = true;
    }
    return isChanged;
  }

  generateUrl(stateOrName: StateOrName, params?: RawParams, options?: HrefOptions): string {
    return this.router.stateService.href(stateOrName, params, options);
  }

  getStateFromUrl<T extends StateDeclaration>(url: string): T {
    return this.router.stateService.get().find((stateDeclaration: StateDeclaration): boolean => {
      const stateObject: StateObject = stateDeclaration.$$state();
      return stateObject.url !== null && stateObject.url.exec(url) !== null;
    }) as T;
  }

  setFallbackState(url: string): void {
    this.router.urlService.rules.otherwise(url);
  }

  setInitialState(url: string): void {
    this.router.urlService.rules.initial(url);
  }

  navigate(stateName: string, params: {[key: string]: unknown} = {}): Promise<boolean> {
    const navigatePromise: Promise<boolean> = new Promise(
      (resolve: (value: PromiseLike<boolean> | boolean) => void) => {
        this.router.stateService.go(stateName, params).then(() => {
          resolve(true);
        });
      }
    );

    return navigatePromise;
  }
}
